import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Plus, 
  Settings, 
  Filter, 
  Search, 
  DollarSign,
  Calendar,
  Building,
  CheckCircle,
  User
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { ServiceForm } from '@/components/services/ServiceForm';
import { serviceService } from '@/lib/api/service.service';
import type { Service, ServiceType, ServiceStatistics } from '@/types/service.types';

export const ServiceManagement: React.FC = () => {
  const { id } = useParams();
  const { t } = useTranslation(['services', 'common']);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<ServiceType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | undefined>(undefined);
  const [stats, setStats] = useState<ServiceStatistics>({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
    total_estimated_cost: 0,
    total_actual_cost: 0
  });

  const eventId = parseInt(id || '0');

  useEffect(() => {
    if (eventId) {
      loadServices();
    }
  }, [eventId]);

  // Load statistics whenever services change
  useEffect(() => {
    loadStatistics();
  }, [services]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await serviceService.getEventServices(eventId);
      setServices(data);
      setError(null);
    } catch (err) {
      setError('Failed to load services');
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      // Compute stats locally from services state
      setStats({
        total: services.length,
        pending: services.filter(s => s.status === 'pending').length,
        confirmed: services.filter(s => s.status === 'confirmed').length,
        completed: services.filter(s => s.status === 'completed').length,
        cancelled: services.filter(s => s.status === 'cancelled').length,
        total_estimated_cost: services.reduce((sum, s) => sum + (s.estimated_cost || 0), 0),
        total_actual_cost: services.reduce((sum, s) => sum + (s.actual_cost || 0), 0)
      });
    } catch (err) {
      console.error('Error loading service statistics:', err);
    }
  };

  const handleServiceSave = () => {
    setShowAddForm(false);
    setEditingService(undefined);
    loadServices();
    // loadStatistics will be called automatically when services update
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingService(undefined);
  };

  const handleMarkCompleted = async (serviceId: number) => {
    try {
      await serviceService.updateService(serviceId, { status: 'completed' });
      loadServices();
      // loadStatistics will be called automatically when services update
    } catch (err) {
      setError('Failed to update service status');
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await serviceService.deleteService(serviceId);
        loadServices();
        // loadStatistics will be called automatically when services update
      } catch (err) {
        setError('Failed to delete service');
      }
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.vendor?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || service.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || service.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const vendorCount = new Set(services.map((service) => service.vendor_id)).size;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{t('common:status.loading')}</div>
      </div>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />
      <HeroQuickMenuCard
        borderClassName="border-primary/20"
        heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
        heroContent={
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
                <Settings className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">{t('services:title')}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{t('services:myServices')}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('common:actions.add')}
              </Button>
            </div>
          </div>
        }
        columns={6}
        items={[
          {
            key: 'total-services',
            icon: Settings,
            iconClassName: 'text-blue-600',
            hoverClassName: 'hover:bg-blue-50',
            label: 'Total',
            value: stats.total,
          },
          {
            key: 'pending-services',
            icon: Calendar,
            iconClassName: 'text-amber-600',
            hoverClassName: 'hover:bg-amber-50',
            label: 'Pending',
            value: stats.pending,
          },
          {
            key: 'confirmed-services',
            icon: CheckCircle,
            iconClassName: 'text-cyan-600',
            hoverClassName: 'hover:bg-cyan-50',
            label: 'Confirmed',
            value: stats.confirmed,
          },
          {
            key: 'completed-services',
            icon: CheckCircle,
            iconClassName: 'text-emerald-600',
            hoverClassName: 'hover:bg-emerald-50',
            label: 'Completed',
            value: stats.completed,
          },
          {
            key: 'estimated-total',
            icon: DollarSign,
            iconClassName: 'text-violet-600',
            hoverClassName: 'hover:bg-violet-50',
            label: 'Est. Cost',
            value: formatCurrency(stats.total_estimated_cost),
          },
          {
            key: 'vendors',
            icon: Building,
            iconClassName: 'text-fuchsia-600',
            hoverClassName: 'hover:bg-fuchsia-50',
            label: 'Vendors',
            value: vendorCount,
          },
        ]}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={t('common:actions.search') + '...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as ServiceType | 'all')}
            aria-label="Filter services by type"
            title="Filter services by type"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="catering">Catering</option>
            <option value="photography">Photography</option>
            <option value="music">Music</option>
            <option value="decoration">Decoration</option>
            <option value="transportation">Transportation</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            aria-label="Filter services by status"
            title="Filter services by status"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="invited">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Services List */}
      {filteredServices.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
            <p className="text-gray-500 mb-4">
              {services.length === 0 
                ? "Get started by adding your first service."
                : "No services match your current filters."}
            </p>
            {services.length === 0 && (
              <Button onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Service
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map((service) => (
            <Card key={service.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{service.name}</CardTitle>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                    {service.status}
                  </span>
                </div>
                <CardDescription className="text-sm text-gray-600">
                  {service.type} {service.description && `- ${service.description}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {service.vendor && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Building className="h-4 w-4 mr-2" />
                      Vendor: {service.vendor.name}
                      {service.vendor.contact_person && ` (${service.vendor.contact_person})`}
                    </div>
                  )}
                  
                  {service.managed_by_id && (
                    <div className="flex items-center text-sm text-gray-500">
                      <User className="h-4 w-4 mr-2" />
                      Managed by: User ID {service.managed_by_id}
                    </div>
                  )}
                  
                  {service.service_date && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(service.service_date).toLocaleDateString()}
                    </div>
                  )}
                  
                  {service.estimated_cost && (
                    <div className="flex items-center text-sm text-gray-500">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Est: {formatCurrency(service.estimated_cost)}
                      {service.actual_cost && (
                        <span className="ml-2 text-green-600">
                          | Actual: {formatCurrency(service.actual_cost)}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {service.notes && (
                    <div className="text-sm text-gray-500">
                      Notes: {service.notes}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => setEditingService(service)}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleMarkCompleted(service.id)}
                    disabled={service.status === 'completed'}
                    title="Mark as completed"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDeleteService(service.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete service"
                  >
                    ×
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Service Form Modal */}
      {(showAddForm || editingService) && (
        <ServiceForm
          eventId={eventId}
          service={editingService}
          isOpen={showAddForm || !!editingService}
          onClose={handleCloseForm}
          onSave={handleServiceSave}
        />
      )}
    </PageContainer>
  );
};
